import { Injectable } from "@angular/core";
import { CommonApiService } from "./common-api.service";
import { APIURL } from "../global/constant";
import { Observable } from "rxjs";

/** Whether a destination slot is a catalog view or a state variant. */
export type ImportSlotKind = "catalog" | "state";

/** One importable destination slot from `GET import-look-views`. */
export interface ImportLookViewSlot {
  /** `entity_look_view_master.id`. Not unique across state slots. */
  id: number | null;
  view_key: string;
  label: string;
  description?: string | null;
  sort_order: number;
  is_identity_frame: boolean;
  is_mandatory: boolean;
  /**
   * When false, batch generation and option-approve skip this slot
   * (independent of is_mandatory). Defaults to true on the backend.
   */
  is_generation_required?: boolean;
  view_type: "anchor" | "look" | "state" | string;
  slot_kind: ImportSlotKind | string;
  /** Required on import when `slot_kind === "state"`. */
  state_id?: string | null;
  variant_category?: string | null;
  variant_value?: string | null;
}

/** A look and the slots that can receive an imported image. */
export interface ImportLook {
  /** `entity_images.id`. Sent as `image_id` on import, despite the name. */
  id: number;
  look_name: string;
  look_type: string;
  is_anchorlook: boolean;
  llm_id?: string | null;
  views: ImportLookViewSlot[];
}

/** One row of the multi-import request. */
export interface ImportLibraryImageView {
  /** The look's id (`looks[].id` from the picker). */
  image_id: number;
  /** Source GCS URL of the library image being copied. */
  image_url: string;
  look_view_master_id?: number | null;
  /** Mandatory when the target slot is a state. */
  state_id?: string | null;
  source_variation_id?: number | null;
}

/** A slot the backend could not fill, returned alongside 200. */
export interface ImportSkippedItem {
  item: ImportLibraryImageView;
  reason: string;
}

@Injectable({
  providedIn: "root",
})
export class MoodboardService {
  constructor(private commonApiService: CommonApiService) {}

  fetchEntityVerifier(videoId: number | string): Observable<any> {
    return this.commonApiService.get(APIURL.entityVerifier + videoId);
  }

  getMoodboardExcelData(moodboardId: number | string): Observable<any> {
    return this.commonApiService.get(
      `${APIURL.moodboardExcelData}/${moodboardId}`,
    );
  }

  /** POST body: full entity-verifier payload + `video_id` (see moodboard confirm gate). */
  postEntityConfirmation(payload: any): Observable<any> {
    return this.commonApiService.post(APIURL.entityConfirmation, payload);
  }

  fetchMoodboardEntityList(videoId: number | string): Observable<any> {
    return this.commonApiService.get(APIURL.moodboardEntityList + videoId);
  }

  /** Manually add a visual element that was not found during script scanning. */
  manualAddEntity(payload: {
    video_id: number | string;
    entity_type: string;
    entity_version_id?: number | string | null;
    name: string;
    role?: string;
    description?: string;
    is_human: boolean;
  }): Observable<any> {
    return this.commonApiService.post(APIURL.moodboardEntityManualAdd, payload);
  }

  fetchEntitiesByVideoId(videoId: number | string): Observable<any> {
    return this.commonApiService.get(APIURL.moodboardEntitiesByVideo + videoId);
  }

  fetchEntitiesByType(payload: {
    video_id: number | string;
    entity_type: string;
  }): Observable<any> {
    return this.commonApiService.post(APIURL.moodboardEntitiesByType, payload);
  }

  onSavingExtractedEntities(payload: any, videoId: number | string) {
    return this.commonApiService.post(
      APIURL.saveExtractedEntities + videoId,
      payload,
    );
  }

  getEntityDetails(payload: any) {
    const param = this.commonApiService.getParamsFromObj(payload);
    return this.commonApiService.get(APIURL.entityDetails, param);
  }

  getEditEntityOptions(payload: any) {
    const params = this.commonApiService.getParamsFromObj(payload);
    return this.commonApiService.get(APIURL.editEntityOptions, params);
  }

  onSavingEntityAttributes(payload: any) {
    return this.commonApiService.post(APIURL.saveEntityAttributes, payload);
  }

  onAddingEntityReference(payload: any) {
    return this.commonApiService.post(APIURL.entityReferenceAdd, payload);
  }

  createEntityLookV2(payload: {
    version_id: number | string;
    look_name: string;
    look_description: string;
    look_option_id: number | string;
    is_anchorlook: boolean;
  }) {
    return this.commonApiService.post(APIURL.entityReferenceAddV2, payload);
  }

  uploadEntityImage(formData: FormData) {
    return this.commonApiService.post(APIURL.referenceImageUpload, formData);
  }

  uploadEntityImageV2(formData: FormData) {
    return this.commonApiService.post(APIURL.referenceImageUploadV2, formData);
  }

  uploadReferenceImage(payload: {
    moodboard_id: number | string;
    entity_version_id: number | string;
    file_name: string;
    content_type: string;
  }) {
    return this.commonApiService.post(APIURL.moodboardUploadRefImage, payload);
  }

  onEntityImageGeneration(payload: any) {
    return this.commonApiService.post(APIURL.entityImageGenerate, payload);
  }

  onEntityRefImageApproval(payload: any) {
    return this.commonApiService.post(APIURL.entityRefImageApproved, payload);
  }

  /** Preview impact of approving a variation (sibling views / storyboard usage). */
  approveImpactPreview(payload: { variation_id: number }) {
    return this.commonApiService.post(APIURL.approveImpactPreview, payload);
  }

  /**
   * Regenerate sibling views after identity-frame approve.
   * Payload is the `data` object from `/moodboard/approve-impact-preview`.
   */
  regenerateSiblingViews(payload: Record<string, unknown>) {
    return this.commonApiService.post(APIURL.regenerateSiblingViews, payload);
  }

  onEntityImageApproved(payload: any) {
    return this.commonApiService.post(APIURL.entityImageApprove, payload);
  }

  saveEntityImageReference(formData: FormData) {
    return this.commonApiService.post(APIURL.entityImageReference, formData);
  }

  deleteEntityImageReference(referenceId: number | string) {
    return this.commonApiService.delete(
      `${APIURL.entityImageReference}/${referenceId}`,
    );
  }

  /** Flag a variation's image as a reusable reference (`is_reference = true`). */
  addVariationReference(variationId: number | string) {
    return this.commonApiService.post(
      `${APIURL.entityImageVariationReference}${variationId}/reference`,
      {},
    );
  }

  /** Clear the reference flag — the image itself is untouched. */
  removeVariationReference(variationId: number | string) {
    return this.commonApiService.delete(
      `${APIURL.entityImageVariationReference}${variationId}/reference`,
    );
  }

  getEntityImageReferences(
    image_id: number | string,
    look_option_id?: number | string | null,
  ) {
    const params = this.commonApiService.getParamsFromObj({
      image_id,
      ...(look_option_id != null && look_option_id !== ""
        ? { look_option_id }
        : {}),
    });
    return this.commonApiService.get(APIURL.entityImageReferences, params);
  }

  updateImageReadyStatus(payload: {
    image_id: number | string;
    status: string;
    variation_id?: number | string;
    moodboard_id?: number | string | null;
  }) {
    return this.commonApiService.post(
      APIURL.entityImageReadyForReview,
      payload,
    );
  }

  updateEntityLookOptionStatus(payload: {
    entity_version_id: number | string;
    look_option_id: number | string;
    status: string;
  }) {
    return this.commonApiService.post(APIURL.entityLookOptionStatus, payload);
  }

  lockUnlockEntityVersion(payload: any) {
    return this.commonApiService.post(APIURL.entityVersionLockUnlock, payload);
  }

  setEntityVersionDisabled(payload: {
    entity_version_id: number | string;
    disabled: boolean;
    moodboard_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.entityVersionDisabled, payload);
  }

  deleteEntityImage(payload: any) {
    return this.commonApiService.post(APIURL.entityImageDelete, payload);
  }

  updateEntityImageViewStatus(payload: {
    image_id: number | string;
    variation_id: number | string;
  }) {
    return this.commonApiService.post(
      APIURL.entityImageUpdateViewStatus,
      payload,
    );
  }

  onEntityAutonomousDnaImage(payload: any) {
    return this.commonApiService.post(APIURL.entityAutonomousDnaImage, payload);
  }

  upscaleMoodboardImage(payload: {
    upscaleFactor: "x2" | "x3" | "x4";
    model_id: number | string;
    image_id: number | string;
    version_id: number | string;
    video_id: number | string;
    entity_imgvariations_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.upscaleMoodboardImage, payload);
  }

  deleteEntityLookView(payload: {
    entity_look_view_id: number | string;
    moodboard_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.entityLookViewDelete, payload);
  }

  disableEntityLookView(payload: {
    entity_look_view_id: number | string;
    entity_version_id: number | string;
    moodboard_id: number | string;
    disabled: boolean;
  }) {
    return this.commonApiService.post(APIURL.entityLookViewDisable, payload);
  }

  createEntityLookView(payload: {
    entity_version_id: number | string;
    look_option_id: number | string;
    look_option_look_id: number | string;
    title: string;
    description?: string;
    look_view_master_id?: number | string | null;
    moodboard_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.entityLookViewCreate, payload);
  }

  getEntityVersionOptionApprovedViews(payload: {
    entity_version_id: number | string;
  }) {
    const params = this.commonApiService.getParamsFromObj(payload);
    return this.commonApiService.get(
      APIURL.entityVersionOptionApprovedViews,
      params,
    );
  }

  generateAllReferences(payload: {
    video_id: number | string;
    moodboard_id: number | string;
    model_id?: number | string;
    variations?: number;
    notify_trigger?: string;
    user_instruction?: string;
    scope?: "all" | "no-look";
    /** Nested selection: entity_type → entity_id → option_id → looks → views */
    [entityType: string]: unknown;
  }) {
    return this.commonApiService.post(
      APIURL.autonomousGenerationMoodboard,
      payload,
    );
  }

  updateEntityVersionDescription(payload: {
    entity_version_id: number | string;
    moodboard_id: number | string;
    description: string;
  }) {
    return this.commonApiService.post(
      APIURL.entityVersionUpdateDescription,
      payload,
    );
  }

  updateEntityImageDescription(payload: {
    entity_version_id: number | string;
    moodboard_id: number | string;
    image_id: number | string;
    description: string;
  }) {
    return this.commonApiService.post(
      APIURL.entityImageUpdateDescription,
      payload,
    );
  }

  getVariationEditRecipes(variationId: number | string): Observable<any> {
    return this.commonApiService.get(
      `${APIURL.variationRecipeFetch}/${variationId}`,
    );
  }

  /**
   * Destination slots for a library import: looks, each with its importable
   * views. State slots share a `look_view_master_id`, so callers must key by
   * master id + `state_id`, never master id alone.
   */
  getImportLookViews(payload: {
    entity_version_id: number;
    moodboard_id: number;
  }): Observable<any> {
    const params = this.commonApiService.getParamsFromObj(payload);
    return this.commonApiService.get(APIURL.entityImportLookViews, params);
  }

  /**
   * Create a new option and import one or more library images into its views.
   * Returns 200 even with partial failures — always check `data.skipped`.
   */
  importLibraryImages(payload: {
    entity_version_id: number;
    moodboard_id: number;
    video_id?: number;
    views: ImportLibraryImageView[];
    justification?: string;
  }): Observable<any> {
    return this.commonApiService.post(
      APIURL.entityImportLibraryImages,
      payload,
    );
  }

  /** Import a single library image onto an existing option view. */
  importLibraryImageToLookView(payload: {
    entity_version_id: number;
    look_option_id: number;
    look_option_look_id: number;
    entity_look_view_id: number;
    image_url: string;
    video_id?: number;
    moodboard_id?: number;
    source_variation_id?: number;
    justification?: string;
  }): Observable<any> {
    return this.commonApiService.post(
      APIURL.entityLookViewImportLibraryImage,
      payload,
    );
  }

  /** GET `/moodboard/structure-sync/:video_id/:moodboard_id` — banner, issues, holds. */
  getStructureSync(
    videoId: number | string,
    moodboardId: number | string,
  ): Observable<any> {
    return this.commonApiService.get(
      `${APIURL.moodboardStructureSync}${videoId}/${moodboardId}`,
    );
  }

  /** POST `/moodboard/structure-sync/:video_id/:moodboard_id` — trigger sync. */
  triggerStructureSync(
    videoId: number | string,
    moodboardId: number | string,
    body?: { mode?: "gate" | "resync" },
  ): Observable<any> {
    return this.commonApiService.post(
      `${APIURL.moodboardStructureSync}${videoId}/${moodboardId}`,
      body ?? {},
    );
  }

  /** POST `/moodboard/structure-sync/:video_id/:moodboard_id/action`. */
  postStructureSyncAction(
    videoId: number | string,
    moodboardId: number | string,
    payload: {
      issue_id: string;
      action_id: string;
      target?: Record<string, unknown>;
      decision?: Record<string, unknown>;
      entity_look_view_id?: number | string;
      rerun?: boolean;
      scenes?: number[];
    },
  ): Observable<any> {
    return this.commonApiService.post(
      `${APIURL.moodboardStructureSyncAction}${videoId}/${moodboardId}/action`,
      payload,
    );
  }
}
